/**
 * Tiny fetch wrapper for the admin dashboard.
 *
 * Posts directly to the Cloudflare Worker (single-origin, CORS allowed by
 * the Worker's middleware). Bearer token is held in `localStorage` and
 * attached as `Authorization: Bearer <token>` on every request — the
 * Pages-Function-with-cookie flow is unused in production because Pages
 * Functions deployment was hitting a "non-JSON upstream" failure mode that
 * blocked sign-in. Direct browser → Worker is simpler and works.
 *
 * - Auto-decodes JSON response bodies on success (or returns `null` for
 *   `204 No Content`).
 * - On non-2xx, throws a typed `ApiError` carrying the upstream `code`,
 *   `message`, and `requestId` so the global error boundary surfaces them.
 * - Network-down behavior: if `navigator.onLine === false` we short-circuit
 *   with `ApiError(network_down)`. Mutating methods optionally enqueue
 *   themselves via `replayWhenOnline`.
 * - 401 handling: pops the re-auth modal, then retries the original request
 *   exactly once after the user signs in again.
 * - Toast surfacing: any thrown `ApiError` pushes a toast with the upstream
 *   `requestId`. Pass `silent: true` to opt out.
 */
import { useToastStore } from '../components/Toast';
import { useQueuedMutationsStore } from './queuedMutations';
import { useReauthStore } from './reauth';
import { getStoredToken } from './tokenStore';

const WORKER_ORIGIN = 'https://skey-worker.sonhangtravel.workers.dev';

export interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
  };
  // Some Worker responses (e.g. validation 400s) include extra fields; keep
  // them around so the UI can render specific messages without losing data.
  [extra: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    const code = body.error?.code ?? 'unknown_error';
    const message = body.error?.message ?? `Request failed with status ${status}`;
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = body.error?.requestId ?? null;
    this.body = body;
  }
}

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  /** JSON-serialized automatically when present and not already a string/FormData/Blob/etc. */
  body?: unknown;
  /**
   * When `true`, suppress the global toast that `apiFetch` would otherwise
   * push on `ApiError`. The error is still thrown so callers can render
   * their own inline message.
   *
   * Used for: silent retries (e.g. background refetches), the sign-in form
   * (which renders its own generic message per Requirement 20.3), and the
   * re-auth modal's retry (which would otherwise double-toast).
   */
  silent?: boolean;
  /**
   * When `true` and the browser reports offline, enqueue the request into
   * the `queuedMutations` store and resolve the returned promise once the
   * replay succeeds. Only valid for mutating methods. Defaults to `false`
   * — most callers prefer to fail fast and let the user retry manually.
   */
  replayWhenOnline?: boolean;
  /** Internal: skip the 401 → re-auth flow on this call to avoid loops. */
  _skipReauth?: boolean;
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function isBodyInit(value: unknown): value is BodyInit {
  return (
    typeof value === 'string' ||
    value instanceof FormData ||
    value instanceof Blob ||
    value instanceof ArrayBuffer ||
    value instanceof URLSearchParams ||
    (typeof ReadableStream !== 'undefined' && value instanceof ReadableStream)
  );
}

function pushApiToast(error: ApiError): void {
  // Read the store imperatively so this module stays callable from
  // non-React code (e.g. the TanStack Query `onError` hook).
  useToastStore.getState().addToast({
    severity: 'error',
    title: 'Lỗi',
    message: error.message,
    requestId: error.requestId,
  });
}

/**
 * Issue a request to a relative admin path and decode the JSON response.
 *
 * Throws `ApiError` with status, code, message, requestId on non-2xx.
 */
export async function apiFetch<T = unknown>(
  path: string,
  init: ApiRequestInit = {},
): Promise<T> {
  const {
    body,
    headers,
    silent = false,
    replayWhenOnline = false,
    _skipReauth = false,
    ...rest
  } = init;

  const method = (rest.method ?? 'GET').toUpperCase();

  // Offline guard. Mutations may opt in to the replay queue; everything
  // else throws a typed error so the network banner and the toast layer
  // stay consistent (Requirement 25.2 / 32.1).
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    if (replayWhenOnline && MUTATING_METHODS.has(method)) {
      return new Promise<T>((resolve, reject) => {
        useQueuedMutationsStore.getState().enqueue({
          description: `${method} ${path}`,
          run: async () => {
            try {
              const result = await apiFetch<T>(path, {
                ...init,
                replayWhenOnline: false,
              });
              resolve(result);
            } catch (err) {
              reject(err);
              throw err;
            }
          },
        });
      });
    }
    const networkError = new ApiError(0, {
      error: {
        code: 'network_down',
        message: 'Mất kết nối tới máy chủ',
      },
    });
    if (!silent) {
      pushApiToast(networkError);
    }
    throw networkError;
  }

  const finalHeaders = new Headers(headers);
  let finalBody: BodyInit | undefined;

  if (body !== undefined && body !== null) {
    if (isBodyInit(body)) {
      finalBody = body;
    } else {
      // Default to JSON encoding for plain objects.
      finalBody = JSON.stringify(body);
      if (!finalHeaders.has('content-type')) {
        finalHeaders.set('content-type', 'application/json');
      }
    }
  }

  if (!finalHeaders.has('accept')) {
    finalHeaders.set('accept', 'application/json');
  }

  // Attach bearer token from local storage. Sign-in stores it; sign-out
  // clears it. The 401 → reauth handler retries with a fresh token.
  const token = getStoredToken();
  if (token && !finalHeaders.has('authorization')) {
    finalHeaders.set('authorization', `Bearer ${token}`);
  }

  // Resolve relative paths against the Worker origin. Absolute URLs pass
  // through unchanged (used by tests / mocks).
  const requestUrl = path.startsWith('http')
    ? path
    : `${WORKER_ORIGIN}${path}`;

  let res: Response;
  try {
    res = await fetch(requestUrl, {
      ...rest,
      headers: finalHeaders,
      body: finalBody,
    });
  } catch (cause) {
    // Network-level failure (DNS, abort, TLS). Surface as `network_down`
    // for parity with the offline guard above.
    const networkError = new ApiError(0, {
      error: {
        code: 'network_down',
        message: 'Mất kết nối tới máy chủ',
      },
    });
    if (!silent) {
      pushApiToast(networkError);
    }
    throw networkError;
  }

  if (res.status === 204) {
    return null as T;
  }

  // Best-effort JSON parse; tolerate empty or non-JSON bodies in error cases.
  const contentType = res.headers.get('content-type') ?? '';
  const parsed: unknown = contentType.includes('application/json')
    ? await res.json().catch(() => ({}))
    : await res.text().catch(() => '');

  if (!res.ok) {
    const errorBody: ApiErrorBody =
      typeof parsed === 'object' && parsed !== null
        ? (parsed as ApiErrorBody)
        : { error: { code: 'unknown_error', message: String(parsed || res.statusText) } };
    const error = new ApiError(res.status, errorBody);

    // 401 → pop the re-auth modal, then retry the original request once.
    // The modal preserves any in-flight form state in Zustand
    // (`useReauthStore.formSnapshots`), so the user does not lose work
    // (Requirement 20.4).
    if (error.status === 401 && !_skipReauth) {
      try {
        await useReauthStore.getState().requestReauth();
      } catch {
        if (!silent) {
          pushApiToast(error);
        }
        throw error;
      }
      return apiFetch<T>(path, { ...init, _skipReauth: true });
    }

    if (!silent) {
      pushApiToast(error);
    }
    throw error;
  }

  return parsed as T;
}
